import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { dayStatusLabel } from "~/components/routine/day-status";
import { DosePips } from "~/components/routine/DosePips";
import { INTL_LOCALE, useLocale } from "~/i18n";
import { dayKey } from "~/lib/calendar";
import type { RoutineDay } from "~/lib/routine";

/**
 * The last seven days as a strip: the habit the dashboard card is there to build.
 *
 * Read-only on purpose. The card's tiles are for today; changing an earlier day is a
 * deliberate act that belongs on the routine screen, one tap further in.
 */
export function RoutineWeek({
  days,
  index,
  today,
}: {
  days: Date[];
  index: Map<string, RoutineDay>;
  today: Date;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const intlLocale = INTL_LOCALE[locale];
  const weekday = new Intl.DateTimeFormat(intlLocale, { weekday: "short" });
  const fullDate = new Intl.DateTimeFormat(intlLocale, { dateStyle: "full" });
  const todayKey = dayKey(today);

  return (
    <Box
      component="ul"
      data-testid="routine.week"
      sx={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 0.5,
      }}
    >
      {days.map((day) => {
        const key = dayKey(day);
        const doses = index.get(key);
        const isToday = key === todayKey;
        return (
          <Box
            component="li"
            key={key}
            aria-label={dayStatusLabel(t, fullDate.format(day), doses)}
            data-testid={`routine.week-${key}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
              paddingBlock: 0.75,
              borderRadius: "999px",
              backgroundColor: isToday ? "surface.inset" : "transparent",
            }}
          >
            <Typography
              variant="L3"
              aria-hidden="true"
              sx={{
                color: isToday ? "text.primary" : "text.muted",
                textTransform: "uppercase",
              }}
            >
              {weekday.format(day)}
            </Typography>
            <DosePips doses={doses} />
          </Box>
        );
      })}
    </Box>
  );
}
